/**
 * global.js — Shared utilities. Loaded on every page with `defer`.
 *
 * Deliberately a classic script (not a module) exposing `window.Theme`:
 * component scripts are also classic deferred scripts that register custom
 * elements, so execution order is guaranteed by document order and no module
 * graph or bundler is required. See plan/04-theme-architecture.md §32.2.
 *
 * No dependencies. Keep this file small — it is on the critical path.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Configuration
   * Serialised once by layout/theme.liquid rather than read from data
   * attributes spread across the DOM.
   * ------------------------------------------------------------------ */

  var config = { routes: {}, shop: {}, strings: {} };

  try {
    var configEl = document.getElementById('ThemeConfig');
    if (configEl) config = JSON.parse(configEl.textContent);
  } catch (error) {
    // A malformed config must not take down every component on the page.
    console.error('[theme] Could not parse ThemeConfig', error);
  }

  /* ------------------------------------------------------------------ *
   * Timing
   * ------------------------------------------------------------------ */

  /**
   * Trailing-edge debounce. Used for configurator input and search typing.
   * @param {Function} fn
   * @param {number} wait
   * @returns {Function}
   */
  function debounce(fn, wait) {
    var timer;
    return function debounced() {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  /**
   * rAF-throttled wrapper for scroll and resize handlers, so they can never
   * fire more than once per frame. This is what keeps INP inside budget on
   * the sticky header and sticky add-to-cart bar.
   * @param {Function} fn
   * @returns {Function}
   */
  function throttleFrame(fn) {
    var queued = false;
    var lastArgs;
    return function throttled() {
      lastArgs = arguments;
      if (queued) return;
      queued = true;
      requestAnimationFrame(
        function () {
          queued = false;
          fn.apply(this, lastArgs);
        }.bind(this)
      );
    };
  }

  /* ------------------------------------------------------------------ *
   * Money
   * ------------------------------------------------------------------ */

  var MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  /**
   * Formats cents using the shop's money format string.
   * Mirrors Shopify's own implementation so client-rendered prices (cart
   * drawer, configurator) are byte-identical to server-rendered ones.
   * Any mismatch here reads to a customer as a pricing bug.
   *
   * @param {number} cents
   * @param {string} [format] Overrides the shop money format.
   * @returns {string}
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    var formatString = format || config.shop.money_format || '${{amount}}';
    var match = formatString.match(MONEY_PLACEHOLDER);
    if (!match) return formatString;

    var value = '';

    switch (match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return formatString.replace(MONEY_PLACEHOLDER, value);
  }

  function withDelimiters(cents, precision, thousands, decimal) {
    var number = (Number(cents) || 0) / 100;
    if (!isFinite(number)) number = 0;

    var parts = number.toFixed(precision).split('.');
    var integer = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);

    return parts[1] ? integer + decimal + parts[1] : integer;
  }

  /* ------------------------------------------------------------------ *
   * Accessibility
   * ------------------------------------------------------------------ */

  var announcer = null;
  var announceTimer;

  /**
   * Announces a message to screen readers via the single shared live region
   * in theme.liquid. Owning one region avoids competing live regions talking
   * over each other — the most common AT failure in Shopify themes.
   *
   * The clear-then-set dance is required: setting identical text twice in a
   * row is not re-announced by NVDA or VoiceOver.
   *
   * @param {string} message
   */
  function announce(message) {
    if (!message) return;
    if (!announcer) announcer = document.getElementById('ThemeAnnouncer');
    if (!announcer) return;

    announcer.textContent = '';
    clearTimeout(announceTimer);
    announceTimer = setTimeout(function () {
      announcer.textContent = message;
    }, 150);
  }

  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), summary, iframe, object, embed, ' +
    '[tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  /**
   * Returns the focusable descendants of `container` that are actually
   * reachable — `offsetParent` filters out anything display:none or inside a
   * collapsed accordion, which would otherwise create an invisible focus trap.
   *
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  function getFocusable(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE),
      function (el) {
        return el.offsetParent !== null && !el.hasAttribute('inert');
      }
    );
  }

  /**
   * Traps Tab focus inside `container` and restores it on release.
   * Used by the nav drawer, cart drawer, filter sheet and any modal.
   *
   * Returns a release function; callers MUST call it, including on
   * `pagehide`, or focus stays trapped after a bfcache restore.
   *
   * @param {HTMLElement} container
   * @param {HTMLElement} [initialFocus]
   * @returns {Function} release
   */
  function trapFocus(container, initialFocus) {
    var previouslyFocused = document.activeElement;

    function onKeydown(event) {
      if (event.key !== 'Tab') return;

      var focusable = getFocusable(container);
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus({ preventScroll: true });
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      // Focus has escaped the container entirely — browser chrome, an
      // address-bar round trip, or a programmatic focus() elsewhere. Pull it
      // back to the appropriate edge depending on direction.
      if (!container.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      // Focus may sit on the container itself (tabindex="-1") on open.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Bound to the document, NOT the container. A listener on the container
    // only fires while focus is already inside it, which makes it incapable
    // of recovering focus that has escaped — the one job a focus trap has.
    document.addEventListener('keydown', onKeydown, true);

    var target = initialFocus || getFocusable(container)[0] || container;

    // Force a style flush, then focus SYNCHRONOUSLY.
    //
    // This was previously wrapped in requestAnimationFrame to let the open
    // transition start first. That was wrong: rAF callbacks are throttled or
    // suspended entirely in background and hidden tabs, so focus silently
    // never moved and stayed stranded on <body> — a WCAG 2.4.3 failure with
    // no error anywhere.
    //
    // The flush is still required: focus() on an element inside a
    // `visibility: hidden` ancestor is a no-op, and the caller has only just
    // added the attribute that changes it. Reading offsetHeight forces the
    // recalc before we ask for focus.
    void container.offsetHeight;
    target.focus({ preventScroll: true });

    return function release() {
      document.removeEventListener('keydown', onKeydown, true);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }

  /* ------------------------------------------------------------------ *
   * Scroll locking
   * Reference-counted: a filter sheet opened from inside the cart drawer
   * must not unlock the page when only it closes.
   * ------------------------------------------------------------------ */

  var scrollLocks = 0;
  var savedScrollY = 0;

  function lockScroll() {
    scrollLocks += 1;
    if (scrollLocks > 1) return;

    savedScrollY = window.scrollY;
    // Compensating for the scrollbar prevents the 15px horizontal jump on
    // desktop when the page becomes non-scrollable.
    var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
    document.body.style.position = 'fixed';
    document.body.style.top = -savedScrollY + 'px';
    document.body.style.insetInline = '0';
    document.body.style.paddingInlineEnd = scrollbarWidth + 'px';
  }

  function unlockScroll() {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks > 0) return;

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.insetInline = '';
    document.body.style.paddingInlineEnd = '';
    window.scrollTo(0, savedScrollY);
  }

  /* ------------------------------------------------------------------ *
   * Events
   * A minimal pubsub so components stay decoupled: the configurator does not
   * need a reference to the cart drawer, it just publishes.
   * ------------------------------------------------------------------ */

  var subscribers = {};

  function on(eventName, callback) {
    (subscribers[eventName] = subscribers[eventName] || []).push(callback);
    return function off() {
      subscribers[eventName] = subscribers[eventName].filter(function (cb) {
        return cb !== callback;
      });
    };
  }

  function emit(eventName, detail) {
    (subscribers[eventName] || []).forEach(function (callback) {
      try {
        callback(detail);
      } catch (error) {
        console.error('[theme] Subscriber failed for "' + eventName + '"', error);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Network
   * ------------------------------------------------------------------ */

  /**
   * Standard init for Shopify's AJAX Cart API.
   * `X-Requested-With` is required for Shopify to return JSON errors rather
   * than an HTML error page.
   *
   * @param {Object} body
   * @returns {RequestInit}
   */
  function cartRequest(body) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    };
  }

  var sectionRequests = {};

  /**
   * Fetches a single rendered section. Used to refresh the cart drawer,
   * facet counts and price blocks without a full page load or client-side
   * templating — the server stays the single source of truth for markup.
   *
   * Built with URL/URLSearchParams rather than string concatenation: every
   * caller that matters passes a URL that ALREADY carries a query string
   * (`?filter.v...`, `?page=2`, `?q=`), and naive concatenation produces two
   * `?` characters and a silently wrong response.
   *
   * In-flight requests for the same section are aborted when a newer one
   * starts, so rapid filter clicks can never resolve out of order and paint
   * stale results.
   *
   * Callers MUST tolerate rejection with a DOMException named 'AbortError',
   * which is the normal outcome for a superseded request, not a failure:
   *   .catch(function (e) { if (e.name !== 'AbortError') showError(); })
   *
   * @param {string} sectionId
   * @param {string} [url] Defaults to the current location.
   * @returns {Promise<string>} Rendered section HTML.
   */
  function fetchSection(sectionId, url) {
    var target = new URL(url || window.location.href, window.location.origin);
    target.searchParams.set('section_id', sectionId);

    if (sectionRequests[sectionId]) sectionRequests[sectionId].abort();

    var controller = new AbortController();
    sectionRequests[sectionId] = controller;

    return fetch(target.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: controller.signal
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Section request failed: ' + response.status);
        return response.text();
      })
      .finally(function () {
        if (sectionRequests[sectionId] === controller) delete sectionRequests[sectionId];
      });
  }

  /* ------------------------------------------------------------------ *
   * Environment
   * ------------------------------------------------------------------ */

  var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReducedMotion() {
    return reducedMotionQuery.matches;
  }

  /**
   * True for genuine hover-capable pointers only. Guards hover-reveal
   * behaviour so touch devices never get information locked behind hover.
   */
  function hasHover() {
    return window.matchMedia('(hover: hover)').matches;
  }

  /* ------------------------------------------------------------------ *
   * Public surface
   * ------------------------------------------------------------------ */

  window.Theme = {
    config: config,
    routes: config.routes,
    strings: config.strings,
    debounce: debounce,
    throttleFrame: throttleFrame,
    formatMoney: formatMoney,
    announce: announce,
    getFocusable: getFocusable,
    trapFocus: trapFocus,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    on: on,
    emit: emit,
    cartRequest: cartRequest,
    fetchSection: fetchSection,
    prefersReducedMotion: prefersReducedMotion,
    hasHover: hasHover
  };
})();
